import { useCallback, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
    Alert,
    Anchor,
    Body1,
    Box,
    Flex,
    FullBleed,
    IconChevronRight,
    Stack,
    TextButton,
} from '@myphx-engineering/phx-design-system';

import {
    useFillPathway,
    useFillPrescription,
} from '../../contexts/fill/use-fill-prescription';
import { useFlags } from '../../hooks/use-flags';
import { usePathwayAlerts } from '../../hooks/use-pathway-alerts';
import { usePathwayScreen } from '../../hooks/use-pathway-screen';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { usePatientChoiceMutation } from '../pages/fill/common/use-patient-choice-mutation';

export const PathwayAlertBanner = () => {
    const { pathway, originalPathway } = useFillPathway();
    const { drxId, medication } = useFillPrescription();
    const [searchParams] = useSearchParams();
    const featureFlags = useFlags();

    const pathwayAlert = usePathwayAlerts(medication, pathway);
    const currentScreen = usePathwayScreen();
    const savePatientChoices = usePatientChoiceMutation({ drxId });
    // TODO: replace with subPathway from useFillPathway once available (Bill's next PR)
    const currentSubPathway = searchParams.get('sub-pathway');

    const ctaDestination = useMemo(() => {
        if (!pathwayAlert?.cta) return null;

        const newParams = new URLSearchParams(searchParams.toString());
        newParams.set('pathway', pathwayAlert.cta.target.pathway);

        const origPath = originalPathway || pathway;
        if (origPath) {
            newParams.set('original-pathway', origPath);
        }

        if (pathwayAlert.cta.target.subPathway) {
            newParams.set('sub-pathway', pathwayAlert.cta.target.subPathway);
        }

        return {
            pathname: getAbsoluteRoute(
                pathwayAlert.cta.target.destination === 'pharmacy-select'
                    ? 'fill.drxId.pharmacy.root'
                    : 'fill.drxId.pathSelect',
                { drxId }
            ),
            search: `?${newParams.toString()}`,
        };
    }, [drxId, originalPathway, pathway, pathwayAlert, searchParams]);

    const ctaChoiceKey = pathwayAlert?.cta?.patientChoiceKey;
    const featureFlag = pathwayAlert?.featureFlagName;
    const showBanner = !featureFlag || !!featureFlags[featureFlag];

    const handleCtaClick = useCallback(() => {
        if (!ctaChoiceKey) return;

        void Promise.resolve(
            savePatientChoices({
                pathway: null,
                originalPathway: null,
                subPathway: null,
                additionalChoices: [
                    {
                        patientChoiceKey: ctaChoiceKey,
                        patientChoiceValue: 'true',
                    },
                ],
            })
        ).catch(() => undefined);
    }, [ctaChoiceKey, savePatientChoices]);

    const ctaHref = ctaDestination
        ? `${ctaDestination.pathname}${ctaDestination.search}`
        : null;
    const isInlineCta = !!pathwayAlert?.cta?.inline;

    const screenMatches =
        !pathwayAlert?.screens?.length ||
        pathwayAlert.screens.includes(currentScreen!);

    const subPathwayMatches =
        !pathwayAlert?.subPathways?.length ||
        pathwayAlert.subPathways.includes(currentSubPathway ?? '');

    if (
        !pathwayAlert ||
        !currentScreen ||
        !screenMatches ||
        !subPathwayMatches ||
        !showBanner
    ) {
        return null;
    }

    return (
        <FullBleed color={pathwayAlert.color} spacing="none" breakpoint="sm">
            <Alert
                severity={pathwayAlert.color}
                data-testid="pathway-alert-banner"
            >
                <Stack gap="sm">
                    <Body1>
                        <Trans
                            ns="dynamic"
                            components={{
                                b: <strong />,
                                br: <br />,
                                a: (
                                    <Anchor
                                        href={ctaHref ?? '#'}
                                        onClick={handleCtaClick}
                                        color={pathwayAlert.color}
                                        inherit
                                        bold
                                    />
                                ),
                            }}
                        >
                            {pathwayAlert.content}
                        </Trans>
                    </Body1>
                    {ctaDestination && !isInlineCta ? (
                        <Box>
                            <TextButton
                                link
                                color={pathwayAlert.color}
                                justify="start"
                                to={ctaDestination}
                                onClick={handleCtaClick}
                            >
                                <Flex align="center" gap="xxxs">
                                    <Box>{pathwayAlert.cta!.text}</Box>
                                    <IconChevronRight />
                                </Flex>
                            </TextButton>
                        </Box>
                    ) : null}
                </Stack>
            </Alert>
        </FullBleed>
    );
};
